import React, { useEffect, useRef, useState } from "react";


function Newzealandimport() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const contentRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 5);

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          setActiveIndex(index);
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (index) => {
    setActiveIndex(index);
    const section = sectionRefs.current[index];
    const contentElement = contentRef.current;

    const scrollPosition = section.offsetTop - contentElement.offsetTop;

    contentElement.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-wrap">
      <div className="mb-20 mt-20 flex min-h-screen">
        <div
          ref={titleRef}
          className="sticky top-20 hidden h-screen w-1/4 overflow-y-auto bg-gray-100 p-4 md:block"
        >
          <ul className="space-y-2">
            <li
              onClick={() => scrollToSection(0)}
              className={`cursor-pointer p-8 ${
                activeIndex === 0
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Working Holiday Visa
            </li>
            <li
              onClick={() => scrollToSection(1)}
              className={`cursor-pointer p-8 ${
                activeIndex === 1
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Study Visa
            </li>
            <li
              onClick={() => scrollToSection(2)}
              className={`cursor-pointer p-8 ${
                activeIndex === 2
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Dependent Child Student Visa
            </li>
            <li
              onClick={() => scrollToSection(3)}
              className={`cursor-pointer p-8 ${
                activeIndex === 3
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Business Visa
            </li>
            <li
              onClick={() => scrollToSection(4)}
              className={`cursor-pointer p-8 ${
                activeIndex === 4
                  ? "bg-blue-500 text-white"
                  : "bg-white text-[#2C3F69]"
              } shadow rounded-md`}
            >
              Permanent Resident Visa
            </li>
          </ul>
        </div>

        <div
          ref={contentRef}
          className="mb-10 h-screen overflow-y-auto p-4 md:w-3/4 lg:w-3/4 xl:w-3/4"
        >
          <div ref={(el) => (sectionRefs.current[0] = el)}>
            <p className="text-xl font-bold text-[#011473]">
              Working Holiday Visa
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              This visa allows young individuals to work in New Zealand for up to 12 months on a temporary basis. It is typically available for individuals aged 18-30, with some countries offering eligibility up to 35 years of age. Citizens of the UK and Canada may be eligible for an extension of up to 24 months.
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              Applicants must demonstrate sufficient funds to support their stay and provide proof of a job offer or employment contact. The application process generally takes around 30 days.
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              This visa is available to citizens of countries that have a working holiday agreement with New Zealand.
            </p>
          </div>

          <div ref={(el) => (sectionRefs.current[1] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">Study Visa</p>
            <p className="mt-4 text-md text-[#2C3F69]">
              A Study Visa is required for individuals intending to pursue education in New Zealand for more than 3 months. Applicants should select their area of study and university based on their academic qualifications, language proficiency and other skills.
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              Applicants over the age of 17 must provide an acceptance letter from a recognized institution, proof of sufficient funds and a clean criminal background check. A medical examination may also be required to ensure health and safety standards.
            </p>
            <p className="mt-4 text-md text-[#2C3F69] font-bold">
              Key Requirements for a Study Visa:
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Proof of academic qualifications</li>
              <li>Letter of acceptance from an educational institution</li>
              <li>Evidence of sufficient financial resources</li>
              <li>Valid passport</li>
              <li>Health and criminal background checks</li>
            </ul>
          </div>

          <div ref={(el) => (sectionRefs.current[2] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">
              Dependent Child Student Visa
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              This visa is available for children whose parents are either working or studying in New Zealand. These children can enjoy free education until the secondary level and are permitted to reside in New Zealand until they reach the age of 19.
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              Once the child reaches tertiary education, they will need to apply for a fee-paying student visa. In some cases, a tuberculosis test may be required if the stay exceeds a certain duration.
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              Children of parents pursuing a PhD or those with financial support from a New Zealand sponsor may be eligible for this visa. Additionally, New Zealand offers opportunities for students to work part-time while studying and later transition to a work visa post-graduation.
            </p>
          </div>

          <div ref={(el) => (sectionRefs.current[3] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">Business Visa</p>
            <p className="mt-4 text-md text-[#2C3F69]">
              The Business Visa allows entrepreneurs and investors to establish or invest in businesses in New Zealand. This visa can also pave the way for applying for permanent residence if the business proves successful.
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              Applicants must have a proven track record in business management or investment. The visa is available for individuals who have owned or operated a business for at least 6 months, or those who can demonstrate strong potential for economic growth through business investment.
            </p>
            <p className="mt-4 text-md text-[#2C3F69] font-bold">
              Key Requirements:
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Verification of business ownership and experience</li>
              <li>Proof of funds and financial stability</li>
              <li>Valid passport</li>
              <li>Good health and a clean criminal record</li>
            </ul>
          </div>

          <div ref={(el) => (sectionRefs.current[4] = el)} className="mt-10">
            <p className="text-xl font-bold text-[#011473]">
              Permanent Resident Visa
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              After residing in New Zealand for a minimum of two years, individuals can apply for Permanent Residency. This visa grants full rights to live, work and travel freely within the country.
            </p>
            <p className="mt-4 text-md text-[#2C3F69]">
              The Permanent Resident Visa is indefinite and once granted, family members of the visa holder automatically receive the same status. The visa holder enjoys unrestricted travel and access to various benefits provided by New Zealand.
            </p>
            <p className="mt-4 text-lg text-[#2C3F69] font-bold">
              Key Requirements:
            </p>
            <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
              <li>Valid passport</li>
              <li>Evidence of employment and residence in New Zealand</li>
              <li>Proof of financial stability</li>
              <li>Clear health and character checks</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Newzealandimport;
